/**
 * SwipeUI Fetch Service - Step 9: Data Fetch & Caching
 * Feature Bible compliant Wikipedia data fetching service
 */

import { UnifiedWikipediaService, UnifiedServiceOptions } from './UnifiedWikipediaService';
import type { 
  SwipeUIFetchService as ISwipeUIFetchService,
  ArticleId,
  SwipeArticle,
  ServiceOptions,
} from './types';

// Re-export all types for convenience
export * from './types';
export { IdResolver, ArticleIdUtils } from './IdResolver';
export { UnifiedWikipediaService } from './UnifiedWikipediaService';
export { UnifiedCacheManager } from './cache/UnifiedCacheManager';
export { LookupCache } from './cache/LookupCache';

/**
 * Main service interface for SwipeUI
 */
export interface SwipeUIFetchService extends ISwipeUIFetchService {}

/**
 * Default service configuration optimized for Swipe UI usage patterns
 */
const DEFAULT_CONFIG: UnifiedServiceOptions = {
  cacheSize: 500,           // Cache 500 articles (balanced memory usage)
  cacheTTL: 300000,         // 5 minute TTL (good for active gameplay)
  lookupCacheSize: 1000,    // Cache 1000 title↔ID mappings
  enableMetrics: true,      // Enable performance tracking
  timeoutMs: 8000,          // 8 second timeout
  userAgent: 'BlueLinks-SwipeUI/1.0 (Educational Game)',
};

/**
 * Singleton service instance
 */
let serviceInstance: UnifiedWikipediaService | null = null;

/**
 * Get or create the singleton service instance
 */
function getServiceInstance(config?: UnifiedServiceOptions): UnifiedWikipediaService {
  if (!serviceInstance) {
    serviceInstance = new UnifiedWikipediaService({
      ...DEFAULT_CONFIG,
      ...config,
    });
  }
  return serviceInstance;
}

/**
 * Main service instance for SwipeUI components
 */
export const swipeUIFetchService: SwipeUIFetchService = getServiceInstance();

/**
 * Feature Bible compliant getArticle function
 * Direct access to the core functionality
 */
export const getArticle = (id: ArticleId, options?: ServiceOptions): Promise<SwipeArticle> => {
  return swipeUIFetchService.getArticle(id, options);
};

/**
 * Configure the service (must be called before first use)
 */
export function configureSwipeUIService(config: UnifiedServiceOptions): void {
  if (serviceInstance) {
    console.warn('SwipeUI service already initialized. Configuration changes may not take effect.');
  }
  serviceInstance = new UnifiedWikipediaService({
    ...DEFAULT_CONFIG,
    ...config,
  });
}

/**
 * Utility functions for common SwipeUI operations
 */
export const SwipeUIUtils = {
  /**
   * Fetch article with full content for display
   */
  fetchForDisplay: (id: ArticleId): Promise<SwipeArticle> => {
    return getArticle(id, {
      includeContent: true,
      includeLinks: true,
      includeMetadata: true,
      priority: 'normal',
      source: 'swipe-display',
    });
  },

  /**
   * Fetch article metadata only for navigation
   */
  fetchForNavigation: (id: ArticleId): Promise<SwipeArticle> => {
    return getArticle(id, {
      includeContent: false,
      includeLinks: true,
      includeMetadata: true,
      priority: 'high',
      source: 'swipe-navigation',
    });
  },

  /**
   * Prefetch articles that might be needed soon
   */
  prefetchArticles: async (ids: ArticleId[]): Promise<void> => {
    const prefetchPromises = ids.map(id => 
      getArticle(id, {
        includeContent: false,
        includeLinks: false,
        includeMetadata: true,
        priority: 'low',
        source: 'swipe-prefetch',
      }).catch(err => {
        console.warn(`Prefetch failed for ${id}:`, err);
      })
    );

    await Promise.all(prefetchPromises);
  },

  /**
   * Get article links for navigation menu
   */
  getNavigationLinks: (id: ArticleId, limit?: number): Promise<import('./types').ArticleLink[]> => {
    return swipeUIFetchService.getArticleLinks(id, limit);
  },

  /**
   * Convert legacy title-based ID to new prefixed format
   */
  migrateFromLegacyId: (legacyId: string): ArticleId => {
    return require('./IdResolver').IdResolver.fromLegacyId(legacyId);
  },

  /**
   * Get service health and performance stats
   */
  getServiceHealth: () => {
    const service = getServiceInstance();
    return service.getHealthStatus();
  },

  /**
   * Clear all service caches
   */
  clearAllCaches: (): void => {
    const service = getServiceInstance();
    service.clearCache();
  },
} as const;

/**
 * React hook for using the SwipeUI fetch service
 * (Can be implemented later when we update components)
 */
export interface UseSwipeUIFetchOptions extends ServiceOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

// Note: Actual React hook implementation will be added when we update components
export const useSwipeUIFetch = (id: ArticleId | null, options: UseSwipeUIFetchOptions = {}) => {
  // Placeholder - will be implemented during component integration phase
  throw new Error('useSwipeUIFetch hook not yet implemented - use swipeUIFetchService directly for now');
};

/**
 * Development utilities (only available in development builds)
 */
export const DevUtils = process.env.NODE_ENV === 'development' ? {
  /**
   * Get detailed service statistics
   */
  getDetailedStats: () => {
    const service = getServiceInstance();
    return service.getCacheStats();
  },

  /**
   * Force cache cleanup
   */
  forceCacheCleanup: () => {
    const service = getServiceInstance();
    // Access private method through type assertion for development
    (service as any).cacheManager?.cleanupExpired?.();
  },

  /**
   * Inspect cache contents (for debugging)
   */
  inspectCache: () => {
    const service = getServiceInstance();
    const stats = service.getCacheStats();
    console.table(stats);
    return stats;
  },

  /**
   * Test ID resolution
   */
  testIdResolution: (testIds: string[]) => {
    const { IdResolver } = require('./IdResolver');
    const results = testIds.map(id => {
      try {
        const parsed = IdResolver.parse(id);
        const description = IdResolver.describe(id);
        return { id, parsed, description, valid: true };
      } catch (error) {
        return { id, error: error.message, valid: false };
      }
    });
    console.table(results);
    return results;
  },
} : undefined;

/**
 * Service lifecycle management
 */
export const ServiceLifecycle = {
  /**
   * Initialize service with custom configuration
   */
  initialize: (config?: UnifiedServiceOptions): void => {
    configureSwipeUIService(config || {});
  },

  /**
   * Check if service is ready
   */
  isReady: (): boolean => {
    return serviceInstance !== null;
  },

  /**
   * Gracefully shutdown service
   */
  shutdown: (): void => {
    if (serviceInstance) {
      serviceInstance.destroy();
      serviceInstance = null;
    }
  },

  /**
   * Reset service to default state
   */
  reset: (): void => {
    ServiceLifecycle.shutdown();
    serviceInstance = getServiceInstance();
  },
} as const;

// Auto-initialize with default config
if (typeof window !== 'undefined' || typeof global !== 'undefined') {
  // Ensure service is initialized when module is loaded
  getServiceInstance();
}

export default swipeUIFetchService;